import { useRouter } from 'next/router';
import { BaseModalProps } from 'global/Modal/BaseModal';
import useTransactionFormStore from 'global/Securities/stores/useTransactionFormStore';
import dynamic from 'next/dynamic';
import { FC, memo, useCallback, useEffect } from 'react';
import { TransactionFormType } from 'typings/transactionForm';

import tradingModalStore from 'global/TradingModal/store';
import usePortfolioDetailStore from 'global/Securities/stores/usePortfolioDetailStore';
import useGenPortfolio from 'global/Securities/websocket/usePortfolio';
import tradingDataStore from 'lib/stores/tradingData';
import { useSecuritiesStore } from 'global/Securities/websocket/store/securities.store';

import { useFilterStore } from 'global/MainLayout/SideWidget/RunningTrade/store';
import { StockFilterTypeEnum } from 'global/MainLayout/SideWidget/RunningTrade/types';
import { ORDER_TYPE } from 'constants/trading';
import { FastOrderComponent } from 'fast-trade/Module';
import { useRunningTradeStore } from 'global/Datafeed/store/runningTrade';
import { useFliptBoolean } from 'lib/flipt/hooks';
import { FeatureFlagKeys } from 'lib/flipt/constants/flagKeys';
import { FastRunningTrade, Modal } from './styled';
import FastOrderSkeleton from '../FastOrderSkeleton';
import useFastOrderForm from '../stores/useFastOrderStore';
import useFastOrderDatafeed from '../hooks/useFastOrderDatafeed';

interface SwitchFormConfig {
  orderType?: string;
  showTabs?: boolean;
  showOrderbook?: boolean;
}

interface FastOrderProps extends BaseModalProps {
  // For switching back to classic
  switchFormConfig: SwitchFormConfig;
}

interface FastOrderRunningTradeProps {
  hideCode?: boolean;
}

const FastOrderFederation = dynamic(() => import('fast-trade/Module'), {
  ssr: false,
  loading: () => <FastOrderSkeleton />,
}) as FastOrderComponent;

const FastOrderRunningTrade: FC<FastOrderRunningTradeProps> = ({
  hideCode,
}) => (
  <FastRunningTrade
    runningTradeKey="widget"
    minDataLength={50}
    allowInitialData
    hideCode={hideCode}
    hideStockFilter
    hideHeader
    playAlignRight
    fullWidth
    isPro
    isVolumeColor
  />
);

const FastOrderModal: FC<FastOrderProps> = ({ switchFormConfig, ...props }) => {
  const { onCancel } = props;
  const isDuplicateBidofferEnabled = useFliptBoolean(
    FeatureFlagKeys.FAST_ORDER_DUPLICATE_BIDOFFER,
  );

  const showClassicModal = tradingModalStore((s) => s.showModal);
  const isSecuritiesSocketOnline = useSecuritiesStore((s) => s.isSocketOnline);
  const [symbol, setSymbol] = useFastOrderForm((s) => [s.symbol, s.setSymbol]);
  const setFormType = useTransactionFormStore((s) => s.setFormType);
  const [portfolio, orderList, getOrderHistory] = tradingDataStore((s) => [
    s.portfolio?.data,
    s.orderHistory?.data,
    s.getOrderHistory,
  ]);
  const [portfolioDetail, setCurrentSymbol] = usePortfolioDetailStore((s) => [
    s.data,
    s.setCurrentSymbol,
  ]);

  const [onApplyStockFilter] = useFilterStore((s) => [s.onApplyStockFilter]);
  const [clearData] = useRunningTradeStore((s) => [s.clearData]);
  const {
    orderbook: wsOrderbook,
    bestBidOffer,
    iepiev,
    livePrice,
  } = useFastOrderDatafeed(props.visible ? symbol : '');

  const router = useRouter();
  const isNotSecuritiesPage = router.pathname !== '/securities/[category]';
  useGenPortfolio(isNotSecuritiesPage);

  const handleFormTypeChange = (value: TransactionFormType) => {
    setFormType(value);
    showClassicModal({
      symbol,
      orderType: ORDER_TYPE.BUY,
      ...switchFormConfig,
    });
    props.onCancel();
  };

  const handleEmittenChange = useCallback(
    (newSymbol: string) => {
      setSymbol(newSymbol);
      setCurrentSymbol(newSymbol);
      onApplyStockFilter({
        type: newSymbol
          ? StockFilterTypeEnum.Custom
          : StockFilterTypeEnum.AllStock,
        selected: newSymbol ? [newSymbol] : [],
      });
      clearData('widget');
    },
    [setSymbol],
  );

  const handleModalCancel = () => {
    onApplyStockFilter({
      type: StockFilterTypeEnum.AllStock,
      selected: [],
    });
    clearData('widget');
    props.onCancel();
  };

  useEffect(() => {
    if (props.visible) getOrderHistory(true);
  }, [props.visible]);

  useEffect(() => {
    const handleMessageCompass = (event: CustomEvent) => {
      const customEvent = event;

      if (customEvent.detail.name === 'securities-deposit') {
        router.push('/securities/deposit');
        props.onCancel();
      }
    };

    window.addEventListener('compass-global', handleMessageCompass);

    return () => {
      window.removeEventListener('compass-global', handleMessageCompass);
    };
  }, []);

  return (
    <Modal width={1096} hideHeader {...props} onCancel={handleModalCancel}>
      <FastOrderFederation
        formType={TransactionFormType.FastOrder}
        defaultSymbol={symbol}
        onFormTypeChange={handleFormTypeChange}
        onEmittenChange={handleEmittenChange}
        onClose={onCancel}
        rightSection={<FastOrderRunningTrade hideCode={!!symbol} />}
        orderList={orderList}
        portfolio={portfolio}
        portfolioDetail={portfolioDetail}
        wsOrderbook={wsOrderbook}
        bestBidOffer={bestBidOffer}
        iepiev={iepiev}
        livePrice={livePrice}
        securitiesSocketOnline={isSecuritiesSocketOnline}
        disableDuplicateHandler={!isDuplicateBidofferEnabled}
      />
    </Modal>
  );
};

export default memo(FastOrderModal);
